import React, { useEffect, useState } from 'react';
import { UserAuth } from './context/AuthContext'; // Assuming you have an auth context

const Test = () => {
    const [ballots, setBallots] = useState([]);
    const [status, setStatus] = useState('Not connected');
    const [lastBallotId, setLastBallotId] = useState(null);
    const { user } = UserAuth(); // Get token from auth context
    const LOBBY_ID = 'test-lobby';
    const BASE_URL = 'http://localhost:8080';

    useEffect(() => {
        let eventSource = null;
        let reconnectTimeout = null;

        const connectToSSE = () => {
            if (!user?.accessToken) {
                console.log('No user token available, skipping SSE connection');
                setStatus('Waiting for user token...');
                return;
            }

            if (eventSource) {
                console.log('Closing existing SSE connection');
                eventSource.close();
            }

            try {
                console.log('User token:', user.accessToken);
                const sseUrl = `${BASE_URL}/ballot/sse/${LOBBY_ID}?token=${user.accessToken}`;
                console.log('Attempting to connect to SSE:', sseUrl);
                
                eventSource = new EventSource(sseUrl);
                
                eventSource.onopen = () => {
                    console.log('SSE connection established successfully');
                    setStatus('Connected to SSE');
                };

                eventSource.onmessage = (event) => {
                    console.log('Received SSE message:', event.data);
                    try {
                        const newBallots = JSON.parse(event.data);
                        console.log('Parsed ballots data:', newBallots);
                        setBallots(newBallots);
                        if (newBallots.length > 0) {
                            setLastBallotId(newBallots[newBallots.length - 1].id);
                        }
                    } catch (error) {
                        console.error('Error parsing SSE message:', error);
                        console.error('Raw message data:', event.data);
                    }
                };

                eventSource.onerror = (error) => {
                    console.error('SSE Error occurred:', error);
                    console.error('EventSource readyState:', eventSource.readyState);
                    setStatus('SSE Error: ' + error);
                    
                    if (eventSource) {
                        console.log('Closing SSE connection due to error');
                        eventSource.close();
                    }

                    // Clear any existing reconnect timeout
                    if (reconnectTimeout) {
                        clearTimeout(reconnectTimeout);
                    }

                    // Try to reconnect after 5 seconds
                    console.log('Scheduling reconnect in 5 seconds...');
                    reconnectTimeout = setTimeout(() => {
                        console.log('Attempting to reconnect...');
                        connectToSSE();
                    }, 5000);
                };
            } catch (error) {
                console.error('Error creating EventSource:', error);
                setStatus('Error creating SSE connection: ' + error.message);
            }
        };

        connectToSSE();

        // Cleanup on component unmount
        return () => {
            console.log('Component unmounting, cleaning up...');
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
            }
            if (eventSource) {
                console.log('Closing SSE connection on cleanup');
                eventSource.close();
            }
        };
    }, [user?.accessToken]); // Re-run effect when token changes

    const createTestBallot = async () => {
        if (!user?.accessToken) {
            console.log('Cannot create ballot: No user token available');
            setStatus('No user token available');
            return;
        }

        try {
            console.log('Creating new test ballot...');
            const response = await fetch(`${BASE_URL}/ballots`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.accessToken}`
                },
                body: JSON.stringify({
                    title: 'Test Ballot ' + new Date().toLocaleTimeString(),
                    question: {
                        text: 'Test Question',
                        isMultipleChoice: false,
                        voteCountingRule: 'most votes',
                        options: ['Option 1', 'Option 2', 'Option 3']
                    },
                    showVotesPublicly: true,
                    viewGroupID: 1, // Replace with actual group ID
                    voteGroupID: 1, // Replace with actual group ID
                    isGeneralAssemblySplitVote: false
                })
            });

            console.log('Create ballot response status:', response.status);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Create ballot error response:', errorText);
                throw new Error(`Failed to create ballot: ${errorText}`);
            }

            const result = await response.json();
            console.log('Create ballot success:', result);
            setStatus('Created new ballot');
        } catch (error) {
            console.error('Error creating ballot:', error);
            setStatus('Error creating ballot: ' + error.message);
        }
    };

    const closeLastBallot = async () => {
        if (!user?.accessToken) {
            console.log('Cannot close ballot: No user token available');
            setStatus('No user token available');
            return;
        }

        if (!lastBallotId) {
            console.log('Cannot close ballot: No ballot ID available');
            setStatus('No ballot to close');
            return;
        }

        try {
            console.log('Closing ballot:', lastBallotId);
            const response = await fetch(`${BASE_URL}/ballot/${lastBallotId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${user.accessToken}`
                }
            });

            console.log('Close ballot response status:', response.status);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Close ballot error response:', errorText);
                throw new Error(`Failed to close ballot: ${errorText}`);
            }

            const result = await response.json();
            console.log('Close ballot success:', result);
            setStatus('Closed ballot');
        } catch (error) {
            console.error('Error closing ballot:', error);
            setStatus('Error closing ballot: ' + error.message);
        }
    };

    const archiveLastBallot = async () => {
        if (!user?.accessToken) {
            console.log('Cannot archive ballot: No user token available');
            setStatus('No user token available');
            return;
        }

        if (!lastBallotId) {
            console.log('Cannot archive ballot: No ballot ID available');
            setStatus('No ballot to archive');
            return;
        }

        try {
            console.log('Archiving ballot:', lastBallotId);
            const response = await fetch(`${BASE_URL}/ballot/${lastBallotId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${user.accessToken}`
                }
            });

            console.log('Archive ballot response status:', response.status);
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Archive ballot error response:', errorText);
                throw new Error(`Failed to archive ballot: ${errorText}`);
            }

            const result = await response.json();
            console.log('Archive ballot success:', result);
            setStatus('Archived ballot');
        } catch (error) {
            console.error('Error archiving ballot:', error);
            setStatus('Error archiving ballot: ' + error.message);
        }
    };

    return (
        <div className="container mx-auto p-4 max-w-4xl">
            <h1 className="text-2xl font-bold mb-4">Ballot SSE Test</h1>
            
            <div className="mb-4 flex gap-2">
                <button
                    onClick={createTestBallot}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                    Create Test Ballot
                </button>
                <button
                    onClick={closeLastBallot}
                    className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                >
                    Close Last Ballot
                </button>
                <button
                    onClick={archiveLastBallot}
                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                >
                    Archive Last Ballot
                </button>
            </div>

            <div className="bg-gray-100 p-4 rounded mb-4">
                Status: {status}
            </div>
            
            <div className="space-y-4">
                {ballots.map(ballot => (
                    <div
                        key={ballot.id}
                        className={`p-4 border rounded ${
                            ballot.closed_at ? 'bg-gray-100' : ''
                        } ${
                            ballot.view_group_id === null && ballot.vote_group_id === null
                                ? 'line-through bg-gray-200'
                                : ''
                        }`}
                    >
                        <h3 className="text-lg font-semibold">{ballot.title}</h3>
                        <p>ID: {ballot.id}</p>
                        <p>Status: {ballot.closed_at ? 'Closed' : 'Open'}</p>
                        <p>View Group: {ballot.view_group_id || 'None'}</p>
                        <p>Vote Group: {ballot.vote_group_id || 'None'}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Test;